import { visionLabApi } from '@/app/apiClient';
import type { DeliveryFilterOperator } from '@/features/delivery/types/contracts';
import {
  type CreateDeliveryInput,
  type CreateSegmentInput,
  type DeliveryDownloadLease,
  type DeliveryTask,
  type DeliveryTaskPage,
  type LogicalSegment,
  type LogicalSegmentListResponse,
  type LogicalSegmentPage,
  type SegmentPlan,
  type SegmentPreviewInput,
  createDeliveryInputSchema,
  createSegmentInputSchema,
  segmentPreviewInputSchema,
} from '@/features/mcap/types/contracts';
import type { ApiCallOptions } from '@/shared/api/client';

const DELIVERY_PAGE_LIMIT = 100;

export async function previewSegment(
  assetId: string,
  input: SegmentPreviewInput,
  options: ApiCallOptions = {},
): Promise<SegmentPlan> {
  const body = segmentPreviewInputSchema.parse(input);
  return visionLabApi.post<SegmentPlan>(`/mcaps/${encodeURIComponent(assetId)}/segment-previews`, {
    ...options,
    json: body,
  });
}

export async function createLogicalSegment(
  assetId: string,
  input: CreateSegmentInput,
  options: ApiCallOptions = {},
): Promise<LogicalSegment> {
  const body = createSegmentInputSchema.parse(input);
  return visionLabApi.post<LogicalSegment>(`/mcaps/${encodeURIComponent(assetId)}/segments`, {
    ...options,
    json: body,
  });
}

export async function fetchLogicalSegments(
  assetId: string,
  options: ApiCallOptions = {},
): Promise<LogicalSegmentListResponse> {
  return visionLabApi.get<LogicalSegmentListResponse>(
    `/mcaps/${encodeURIComponent(assetId)}/segments`,
    options,
  );
}

/**
 * Cross-scope MCAP segment search (`GET /mcap-segments`). `query` filters by
 * segment key (CLP-…) case-insensitively; an empty query lists every segment
 * the caller can see. Each hit carries its `assetId`, so callers never need to
 * know the owning asset up front — this replaces asset-first drilling for
 * "which assets have segments".
 */
export async function searchMcapSegments(
  {
    q,
    cursor,
    delivery = 'all',
    deliveryOperator = 'in',
  }: {
    q: string;
    cursor?: string;
    delivery?: string;
    deliveryOperator?: DeliveryFilterOperator;
  },
  options: ApiCallOptions = {},
): Promise<LogicalSegmentPage> {
  return visionLabApi.get<LogicalSegmentPage>('/mcap-segments', {
    ...options,
    searchParams: {
      limit: DELIVERY_PAGE_LIMIT,
      q: q.trim() || undefined,
      cursor: cursor || undefined,
      delivery: delivery !== 'all' ? delivery : undefined,
      deliveryOperator:
        delivery !== 'all' && delivery !== 'none' && deliveryOperator === 'not_in'
          ? 'not_in'
          : undefined,
    },
  });
}

export async function fetchLogicalSegment(
  assetId: string,
  segmentId: string,
  options: ApiCallOptions = {},
): Promise<LogicalSegment> {
  return visionLabApi.get<LogicalSegment>(
    `/mcaps/${encodeURIComponent(assetId)}/segments/${encodeURIComponent(segmentId)}`,
    options,
  );
}

/**
 * Delete a saved logical segment (`DELETE /mcaps/:assetId/segments/:segmentId`). The backend
 * responds with an empty body, so there is nothing to parse — resolves on success, throws an
 * {@link ApiHttpError} otherwise.
 */
export async function deleteLogicalSegment(
  assetId: string,
  segmentId: string,
  options: ApiCallOptions = {},
): Promise<void> {
  await visionLabApi.delete(
    `/mcaps/${encodeURIComponent(assetId)}/segments/${encodeURIComponent(segmentId)}`,
    { ...options, responseType: 'void' },
  );
}

export async function createSegmentDelivery(
  assetId: string,
  segmentId: string,
  input: CreateDeliveryInput,
  options: ApiCallOptions = {},
): Promise<DeliveryTask> {
  const body = createDeliveryInputSchema.parse(input);
  return visionLabApi.post<DeliveryTask>(
    `/mcaps/${encodeURIComponent(assetId)}/segments/${encodeURIComponent(segmentId)}/deliveries`,
    { ...options, json: body },
  );
}

export async function fetchSegmentDelivery(
  assetId: string,
  segmentId: string,
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<DeliveryTask> {
  return visionLabApi.get<DeliveryTask>(
    `/mcaps/${encodeURIComponent(assetId)}/segments/${encodeURIComponent(segmentId)}/deliveries/${encodeURIComponent(deliveryId)}`,
    options,
  );
}

export async function fetchSegmentDeliveries(
  assetId: string,
  segmentId: string,
  { cursor }: { cursor?: string } = {},
  options: ApiCallOptions = {},
): Promise<DeliveryTaskPage> {
  return visionLabApi.get<DeliveryTaskPage>(
    `/mcaps/${encodeURIComponent(assetId)}/segments/${encodeURIComponent(segmentId)}/deliveries`,
    {
      ...options,
      searchParams: { limit: DELIVERY_PAGE_LIMIT, cursor: cursor || undefined },
    },
  );
}

export async function fetchDeliveryDownloadLease(
  assetId: string,
  segmentId: string,
  deliveryId: string,
  options: ApiCallOptions = {},
): Promise<DeliveryDownloadLease> {
  return visionLabApi.get<DeliveryDownloadLease>(
    `/mcaps/${encodeURIComponent(assetId)}/segments/${encodeURIComponent(segmentId)}/deliveries/${encodeURIComponent(deliveryId)}/download`,
    { ...options, cache: 'no-store' },
  );
}
